import { Provide } from '../ioc-container/ioc-container.ts';
import { HttpHandler, HttpMiddleware } from './middleware.ts';
import { inject, multiInject } from 'inversify';
import { HttpResponse } from './response.ts';
import { HttpRequest } from './request.ts';
import { Fetch } from './fetch.ts';
import { FileSaver } from './file-saver.ts';

@Provide()
export class HttpClient {
	constructor(
		@inject(Fetch) private fetch: Fetch,
		@inject(FileSaver) private fileSaver: FileSaver,
		@multiInject(HttpMiddleware) private middlewares: HttpMiddleware[],
	) {}

	async download(url: string, body?: unknown): Promise<void> {
		const request: HttpRequest = {
			method: 'POST',
			headers: new Headers(),
		};
		if (body != null) {
			request.body = JSON.stringify(body);
			request.headers.append('Content-Type', 'application/json');
		}
		const res = await this.call(url, request);

		const blob = await res.blob();
		const filename = this.getFilenameFromHeaders(res);

		this.fileSaver.save(blob, filename);
	}

	private getFilenameFromHeaders(res: HttpResponse): string {
		const disposition = res.headers.get('Content-Disposition');
		const filename: string = disposition
			.split(';')
			.map((p) => p.trim())
			.map((p) => p.split('='))
			.find((p) => p[0] === 'filename')[1];

		return filename;
	}

	async post<T>(url: string, body?: unknown): Promise<T> {
		const request: HttpRequest = {
			method: 'POST',
			headers: new Headers(),
		};
		if (body != null) {
			request.body = JSON.stringify(body);
			request.headers.append('Content-Type', 'application/json');
		}
		const res = await this.call(url, request);

		if (res.status === 200) {
			return await res.json();
		}
	}

	async put<T>(url: string, body?: unknown): Promise<T> {
		const request: HttpRequest = {
			method: 'PUT',
			headers: new Headers(),
		};
		if (body != null) {
			request.body = JSON.stringify(body);
			request.headers.append('Content-Type', 'application/json');
		}
		const res = await this.call(url, request);

		if (res.status === 200) {
			return await res.json();
		}
	}

	async get<T>(url: string): Promise<T> {
		const request: HttpRequest = {
			method: 'GET',
			headers: new Headers(),
		};
		const res = await this.call(url, request);

		if (res.status === 200) {
			return await res.json();
		}
	}

	async delete<T>(url: string, body?: unknown): Promise<T> {
		const request: HttpRequest = {
			method: 'DELETE',
			headers: new Headers(),
		};
		if (body != null) {
			request.body = JSON.stringify(body);
			request.headers.append('Content-Type', 'application/json');
		}
		const res = await this.call(url, request);

		if (res.status === 200) {
			return await res.json();
		}
	}

	private async call(url: string, request: HttpRequest): Promise<HttpResponse> {
		let handler: HttpHandler = new FetchHandler(this.fetch, url);

		for (const middleware of this.middlewares) {
			handler = new MiddlewareHandler(middleware, handler);
		}
		const res = await handler.handle(request);

		if (!res.ok) {
			throw res;
		}

		return res;
	}
}

class FetchHandler implements HttpHandler {
	constructor(
		private fetch: Fetch,
		private url: string,
	) {}

	async handle(request: HttpRequest): Promise<HttpResponse> {
		console.group(`HTTP Fetch: ${this.url}`);
		try {
			return await this.fetch.fetch(this.url, request);
		} finally {
			console.groupEnd();
		}
	}
}

class MiddlewareHandler implements HttpHandler {
	constructor(
		private middleware: HttpMiddleware,
		private next: HttpHandler,
	) {}

	async handle(request: HttpRequest): Promise<HttpResponse> {
		console.group(`HTTP Middleware: ${this.middleware.constructor.name}`);
		try {
			return await this.middleware.handle(request, this.next);
		} catch (err) {
			console.error(err);
			throw err;
		} finally {
			console.groupEnd();
		}
	}
}
